import { useState, useEffect, useCallback, Fragment } from 'react';
import api from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Search, Plus, Trash2, ChevronDown, ChevronRight, Loader2, AlertTriangle, CheckCircle2, Clock, Sparkles } from 'lucide-react';
import MacroLinkDropdown from '@/components/rollup/MacroLinkDropdown';
import CognaraMacroLinkModal from '@/components/construction/CognaraMacroLinkModal';

const STATUS_COLORS = {
  ON_TRACK: { bg: '#10b98115', text: '#10b981', label: 'On Track' },
  DELAYED: { bg: '#ef444415', text: '#ef4444', label: 'Delayed' },
  COMPLETED: { bg: '#22c55e15', text: '#22c55e', label: 'Completed' },
  AHEAD: { bg: '#06b6d415', text: '#06b6d4', label: 'Ahead' },
};
const PRIORITY_COLORS = {
  CRITICAL: { bg: '#ef444420', text: '#ef4444' },
  HIGH: { bg: '#f59e0b20', text: '#f59e0b' },
  MEDIUM: { bg: '#3b82f620', text: '#3b82f6' },
  LOW: { bg: '#94a3b820', text: '#94a3b8' },
};

export default function PCCMRegister({ projectId }) {
  const { isDark } = useTheme();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSystem, setFilterSystem] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showAutoLink, setShowAutoLink] = useState(false);
  const [newEntry, setNewEntry] = useState({ tagNumber: '', itemDescription: '', systemCode: '', subsystemCode: '', discipline: '', priority: 'MEDIUM', area: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterSystem) params.set('system_code', filterSystem);
      if (filterStatus) params.set('status', filterStatus);
      const [entriesRes, sysRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/pccm/entries?${params.toString()}&limit=2000`),
        api.get(`/v1/projects/${projectId}/pccm/systems`),
      ]);
      setEntries(entriesRes.data?.entries || []);
      setTotal(entriesRes.data?.total ?? (entriesRes.data?.entries || []).length);
      setSystems(sysRes.data || []);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, [projectId, search, filterSystem, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    if (!newEntry.tagNumber || !newEntry.itemDescription || !newEntry.systemCode) {
      toast.error('Tag number, description, and system are required');
      return;
    }
    try {
      await api.post(`/v1/projects/${projectId}/pccm/entries`, newEntry);
      toast.success('Entry created');
      setShowAdd(false);
      setNewEntry({ tagNumber: '', itemDescription: '', systemCode: '', subsystemCode: '', discipline: '', priority: 'MEDIUM', area: '' });
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api.delete(`/v1/projects/${projectId}/pccm/entries/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDateChange = async (entryId, stageGateId, field, value) => {
    try {
      await api.put(`/v1/projects/${projectId}/pccm/entries/${entryId}/stage-gates/${stageGateId}`, { [field]: value || null });
      toast.success('Updated');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  return (
    <div className="space-y-3" data-testid="pccm-register">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tag or description..."
            className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border" data-testid="pccm-search-input"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
        </div>
        <select value={filterSystem} onChange={e => setFilterSystem(e.target.value)} data-testid="pccm-filter-system"
          className="px-2 py-1.5 text-xs rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
          <option value="">All Systems</option>
          {systems.map(s => <option key={s.systemCode} value={s.systemCode}>{s.systemCode} - {s.systemName}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="pccm-filter-status"
          className="px-2 py-1.5 text-xs rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
          <option value="">All Statuses</option>
          <option value="ON_TRACK">On Track</option>
          <option value="DELAYED">Delayed</option>
          <option value="COMPLETED">Completed</option>
        </select>
        <Button size="sm" variant="outline" onClick={() => setShowAutoLink(true)} data-testid="pccm-auto-link-btn">
          <Sparkles className="w-3.5 h-3.5 mr-1" /> Cognara Auto-link Macro
        </Button>
        <Button size="sm" onClick={() => setShowAdd(!showAdd)} data-testid="pccm-add-entry-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>

      {showAutoLink && (
        <CognaraMacroLinkModal
          projectId={projectId}
          endpointPrefix="/pccm"
          moduleLabel="Pre-commissioning & Commissioning"
          matchModes={[
            { value: 'ITEM', label: 'Match by tag (item level)' },
            { value: 'SUBSYSTEM', label: 'Match by subsystem (link as a block)' },
          ]}
          onClose={() => setShowAutoLink(false)}
          onCommitted={() => { setShowAutoLink(false); fetchData(); }}
        />
      )}

      {/* Add Form */}
      {showAdd && (
        <div className="p-4 rounded-lg border space-y-3" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input value={newEntry.tagNumber} onChange={e => setNewEntry(p => ({ ...p, tagNumber: e.target.value }))} placeholder="Tag Number *"
              className="px-3 py-1.5 text-sm rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            <input value={newEntry.itemDescription} onChange={e => setNewEntry(p => ({ ...p, itemDescription: e.target.value }))} placeholder="Description *"
              className="px-3 py-1.5 text-sm rounded-lg border col-span-2" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            <select value={newEntry.systemCode} onChange={e => setNewEntry(p => ({ ...p, systemCode: e.target.value }))}
              className="px-3 py-1.5 text-sm rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              <option value="">Select System *</option>
              {systems.map(s => <option key={s.systemCode} value={s.systemCode}>{s.systemCode}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input value={newEntry.discipline} onChange={e => setNewEntry(p => ({ ...p, discipline: e.target.value }))} placeholder="Discipline"
              className="px-3 py-1.5 text-sm rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            <input value={newEntry.area} onChange={e => setNewEntry(p => ({ ...p, area: e.target.value }))} placeholder="Area"
              className="px-3 py-1.5 text-sm rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            <select value={newEntry.priority} onChange={e => setNewEntry(p => ({ ...p, priority: e.target.value }))}
              className="px-3 py-1.5 text-sm rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
            <Button size="sm" onClick={handleAdd} data-testid="pccm-save-entry-btn">Save Entry</Button>
          </div>
        </div>
      )}

      {/* Count */}
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        {entries.length === total ? `${total} item(s)` : `Showing ${entries.length} of ${total} item(s)`}
      </p>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'var(--card-bg-alt)' }}>
              <th className="p-2 text-left w-8" />
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Tag Number</th>
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Description</th>
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>System</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Priority</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Progress</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Status</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Macro Link</th>
              <th className="p-2 text-center font-medium w-10" />
            </tr>
          </thead>
          <tbody>
            {entries.map(entry => {
              const isExpanded = expandedId === entry.id;
              const sc = STATUS_COLORS[entry.overallStatus] || STATUS_COLORS.ON_TRACK;
              const pc = PRIORITY_COLORS[entry.priority] || PRIORITY_COLORS.MEDIUM;
              return (
                <Fragment key={entry.id}>
                  <tr className="cursor-pointer hover:opacity-90 transition-opacity" style={{ borderTop: '1px solid var(--border-color)', background: isExpanded ? 'var(--card-bg-alt)' : 'var(--card-bg)' }}
                    onClick={() => setExpandedId(isExpanded ? null : entry.id)} data-testid={`pccm-entry-row-${entry.tagNumber}`}>
                    <td className="p-2">{isExpanded ? <ChevronDown className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} /> : <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />}</td>
                    <td className="p-2 font-mono text-xs font-semibold" style={{ color: 'var(--accent-primary)' }}>{entry.tagNumber}</td>
                    <td className="p-2" style={{ color: 'var(--text-secondary)' }}>{entry.itemDescription}</td>
                    <td className="p-2 text-xs" style={{ color: 'var(--text-muted)' }}>{entry.systemCode}</td>
                    <td className="p-2 text-center">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: pc.bg, color: pc.text }}>{entry.priority}</span>
                    </td>
                    <td className="p-2 text-center">
                      <div className="flex items-center gap-1.5 justify-center">
                        <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(entry.overallProgress, 100)}%`, background: '#10b981' }} />
                        </div>
                        <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>{entry.overallProgress?.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="p-2 text-center">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: sc.bg, color: sc.text }}>{sc.label}</span>
                    </td>
                    <td className="p-2 text-center">
                      <MacroLinkDropdown
                        projectId={projectId}
                        microModule="WBS"
                        microActivityId={entry.id}
                        microActivityCode={entry.tagNumber}
                        microActivityName={entry.itemDescription}
                      />
                    </td>
                    <td className="p-2 text-center">
                      <button onClick={e => { e.stopPropagation(); handleDelete(entry.id); }} className="p-1 rounded hover:bg-red-500/10" data-testid={`pccm-delete-${entry.tagNumber}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr style={{ background: 'var(--card-bg)' }}>
                      <td colSpan={9} className="p-4" style={{ borderTop: '1px solid var(--border-color)' }}>
                        <div className="space-y-4">
                          {/* Details row */}
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                            <div><span style={{ color: 'var(--text-muted)' }}>Subsystem:</span> <span style={{ color: 'var(--text-primary)' }}>{entry.subsystemCode || '-'}</span></div>
                            <div><span style={{ color: 'var(--text-muted)' }}>Discipline:</span> <span style={{ color: 'var(--text-primary)' }}>{entry.discipline || '-'}</span></div>
                            <div><span style={{ color: 'var(--text-muted)' }}>Area:</span> <span style={{ color: 'var(--text-primary)' }}>{entry.area || '-'}</span></div>
                            <div><span style={{ color: 'var(--text-muted)' }}>Contractor:</span> <span style={{ color: 'var(--text-primary)' }}>{entry.contractor || '-'}</span></div>
                            <div><span style={{ color: 'var(--text-muted)' }}>Punch Items:</span> <span style={{ color: 'var(--text-primary)' }}>{entry.punchItems?.length || 0}</span></div>
                          </div>

                          {/* Stage Gates */}
                          <div>
                            <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Stage Gate Progress</h4>
                            <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr style={{ background: 'var(--card-bg-alt)' }}>
                                    <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Stage Gate</th>
                                    <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Weight</th>
                                    <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Planned Date</th>
                                    <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Forecast Date</th>
                                    <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Actual Date</th>
                                    <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(entry.stageGateProgress || []).map(sg => {
                                    const sgsc = STATUS_COLORS[sg.status] || STATUS_COLORS.ON_TRACK;
                                    return (
                                      <tr key={sg.stageGateId} style={{ borderTop: '1px solid var(--border-color)' }}>
                                        <td className="p-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                                          <div className="flex items-center gap-1.5">
                                            {sg.status === 'COMPLETED' ? <CheckCircle2 className="w-3 h-3 text-green-400" /> :
                                              sg.status === 'OVERDUE' ? <AlertTriangle className="w-3 h-3 text-red-400" /> :
                                              <Clock className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />}
                                            {sg.stageGateName || sg.stageName || sg.stageGateId}
                                          </div>
                                        </td>
                                        <td className="p-2 text-center" style={{ color: 'var(--text-muted)' }} data-testid={`sg-weight-${sg.stageGateId}`}>{sg.weightagePercent ?? 0}%</td>
                                        <td className="p-2 text-center">
                                          <input type="date" value={sg.plannedDate?.slice(0, 10) || ''} data-testid={`sg-planned-${sg.stageGateId}`}
                                            onChange={e => handleDateChange(entry.id, sg.stageGateId, 'plannedDate', e.target.value)}
                                            className="px-1.5 py-0.5 text-[11px] rounded border w-[120px]"
                                            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                                        </td>
                                        <td className="p-2 text-center">
                                          <input type="date" value={sg.forecastDate?.slice(0, 10) || ''} data-testid={`sg-forecast-${sg.stageGateId}`}
                                            onChange={e => handleDateChange(entry.id, sg.stageGateId, 'forecastDate', e.target.value)}
                                            className="px-1.5 py-0.5 text-[11px] rounded border w-[120px]"
                                            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                                        </td>
                                        <td className="p-2 text-center">
                                          <input type="date" value={sg.actualDate?.slice(0, 10) || ''} data-testid={`sg-actual-${sg.stageGateId}`}
                                            onChange={e => handleDateChange(entry.id, sg.stageGateId, 'actualDate', e.target.value)}
                                            className="px-1.5 py-0.5 text-[11px] rounded border w-[120px]"
                                            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                                        </td>
                                        <td className="p-2 text-center">
                                          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: sgsc.bg, color: sgsc.text }}>{sgsc.label}</span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {entries.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No entries found. Click "Add" to create one or seed sample data from Configuration.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
